import { Modal, Button as AntButton } from "antd";
import { useTranslation } from "react-i18next";
import styled from 'styled-components';
import { IDailyMissionDetail, IDailyMissionSummaryClass, IGetDailyMissionDetail } from "../../../../models/canbot.model";
import useAxios from "../../../../hooks/useAxios";
import { useEffect, useState } from "react";
import { getDailyMissionDetail } from "../../../../api/canbot.api";
import moment from "moment";
import React from "react";

interface IProps {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    classCode: string;
    weekSeq: number;
    summaryData?: IDailyMissionSummaryClass;
}

const Block = styled.div`

  .dailymission-script-list {
    display: flex;
    margin-bottom: 0;
  }
  .dailymission-script-list.empty-list {
    min-height: 40px;
  }
  .dailymission-script-list dt, .dailymission-script-list dd {
    line-height: 2;
  }
  .dailymission-script-list dd {
    margin-bottom: 0;
  }
  .dailymission-script-list dt {
    font-weight: 600;
    color: #222;
    width: auto;
  }
  .dailymission-script-list dd {
    color: #666;
    padding-left: 2px;
    flex: 1 0 0%;
    flex-grow: 1;
    text-align: left;
  }
  .dailymission-script-list dd .color-blue {
    color: #5dbecd;
  }
  .dailymission-script-list dd .color-pink {
    color: #bb0665;
  }
  .dailymission-script-list dd .color-red {
    color: #ff4d4f;
    /* font-weight: 600; */
  }

`;

const fnProfanityHighlight = (originalText: string, filteredText: string) : React.ReactNode[] => {
  if (!originalText || !filteredText || originalText === filteredText || originalText.length !== filteredText.length) {
    return [originalText];
  }

  // TextHelper의 범위 계산 재사용
  const ranges = findFilteredRanges(originalText, filteredText);

  if (ranges.length === 0) {
    return [originalText];
  }

  const result: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const { start, end, text } of ranges) {
    if (start > lastIndex) {
      result.push(originalText.slice(lastIndex, start));
    }

    result.push(
      <span key={`highlight-${start}`} className={'color-red'}>
        {text}
      </span>
    );

    lastIndex = end;
  }

  if (lastIndex < originalText.length) {
    result.push(originalText.slice(lastIndex));
  }

  return result;
}

/**
 * 필터링된 범위 찾기
 * @param {string} original - 원본 텍스트
 * @param {string} filtered - 필터된 텍스트 (asterisk로 마스킹)
 * @returns {Array} 필터링된 범위 배열 [{ start, end, text }]
 */
const findFilteredRanges = (original: string, filtered: string) => {
  const ranges = [];
  let currentRange = null;

  for (let i = 0; i < original.length; i++) {
    const isFiltered = filtered[i] === "*";

    if (isFiltered) {
      if (!currentRange) {
        currentRange = { start: i, end: i + 1, text: original[i] };
      } else {
        currentRange.end = i + 1;
        currentRange.text += original[i];
      }
    } else {
      if (currentRange) {
        ranges.push(currentRange);
        currentRange = null;
      }
    }
  }

  // 마지막 범위 처리
  if (currentRange) {
    ranges.push(currentRange);
  }

  return ranges;
}

const DailyMissionDetailModal = ({visible, setVisible, classCode, weekSeq, summaryData}: IProps) => {
    const { t, i18n } = useTranslation();

    const { data: res, isLoading, error, execute } = useAxios<IGetDailyMissionDetail>();

    const [detailDataList, setDetailDataList] = useState<IDailyMissionDetail[]>([]);

    useEffect(() => {
      if(summaryData?.studyCode) {
        execute(getDailyMissionDetail(summaryData?.studyCode, weekSeq));
      }
    }, [summaryData, weekSeq]);

    useEffect(() => {
      if(res && res.data) {
        setDetailDataList(res.data);
      }
    }, [res]);

    return (
        <Modal
            title={'Daily Mission Detail'}
            open={visible}
            onCancel={() => setVisible(false)}
            footer={[
                <div className='comm-btn__area justify-content-center'>
                    <AntButton key="cancel" className='btn-basic gray' onClick={() => setVisible(false)}>
                    {t('common.button.close')}
                    </AntButton>
                </div>
                ]}
            width={1200}
            style={{top:50}}
        >
          <Block>
            {/* 상단 요약 테이블 */}
            <div className="comm-table__area mt-0">
              <table>
                <colgroup>
                  <col width="20%" />
                  <col width="20%" />
                  <col width="20%" />
                  <col width="20%" />
                  <col width="20%" />
                </colgroup>
                <thead>
                  <tr>
                    <th>{t('class.canbot.label6')}</th>
                    <th>{t('class.class.column74')}</th>
                    <th>{t('class.canbot.label2')}</th>
                    <th>{t('class.canbot.label3')}</th>
                    <th>{t('class.canbot.label4')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{weekSeq === 0 ? t('common.text.all') : `Week ${weekSeq}`}</td>
                    <td>{summaryData?.memberName}</td>
                    <td>{summaryData?.missionCount}</td>
                    <td>{summaryData?.passCount}</td>
                    <td>{summaryData?.profanityCount}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 하단 상세 테이블 */}
            <div className="comm-table__area">
              <table>
                <colgroup>
                  <col width="5.35%" />
                  <col width="10.71%" />
                  <col width="10.71%" />
                  <col width="10.71%" />
                  <col width="*" />
                </colgroup>
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Date</th>
                    <th>Pattern</th>
                    <th>Topic</th>
                    <th>Script</th>
                  </tr>
                </thead>
                <tbody>
                  {detailDataList.map((detailData, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{moment(detailData.completeDate).format('YYYY.MM.DD')}</td>
                      <td>{detailData.pattern}</td>
                      <td>{detailData.topic}</td>
                      <td className="script-cell">
                        {detailData.scripts.length > 0 ? (
                          detailData.scripts.map((s, idx, arr) => {
                            const isHighlight =
                              (arr[idx + 1]?.correctAnswerYn === 'Y') ||
                              (arr[idx + 2]?.correctAnswerYn === 'Y');
                            
                            const messageContent = (s.chatRole === 'US' && s.filteredMessageContent) ? fnProfanityHighlight(
                              s.messageContent,
                              s.filteredMessageContent
                            ) : s.messageContent;
                            
                            return (
                              <dl className="dailymission-script-list" key={idx}>
                                <dt>{s.chatRole === 'AI' ? 'CANBOT' : 'Student'} :&nbsp;</dt>
                                <dd>
                                  {isHighlight ? (
                                    <span className="color-blue">{messageContent}</span>
                                  ) : (
                                    messageContent
                                  )}
                                </dd>
                              </dl>
                            );
                          })
                        ) : (
                          <dl className="dailymission-script-list empty-list"></dl>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Block>
        </Modal>
    )
}

export default DailyMissionDetailModal;